/**
 * Nouvelle tournée — UN volet (lot PL3, ADR-0015) : « les mêmes machines d'un même site, tous les N mois ».
 *
 * On tape le site, on coche les machines (celles qui ont une gamme le sont d'emblée ; une machine sans gamme reste décochée et
 * le volet le dit), on choisit la nature, la fréquence et le premier jour, puis qui y va — ou PERSONNE (« À affecter » : ce n'est
 * pas toujours le même technicien qui y va ; les visites ont alors une date et tombent sur la ligne « À affecter » de la grille).
 * Deux remarques distinctes : celle de la TOURNÉE (permanente, lue sur chaque visite) et celle de la PREMIÈRE visite (« ce mois-ci,
 * regarder la quantité d'huile qui reste sur site »). Les pièces en plus « au cas où » se demandent machine par machine et ne
 * concernent que la première visite : ce qui revient à chaque fois appartient à la gamme.
 *
 * Les dates viennent du SERVEUR (un samedi ou un dimanche glisse au lundi) : le volet n'en recalcule aucune, il montre ce qu'il rend.
 * La création est rejouable (clé tirée à l'ouverture) : un double clic ne crée pas deux tournées. Aucun montant.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { ApiError, api, type Asset, type NatureBon, type Site, type Technicien, type Tournee } from '../api/client';
import { aujourdhuiLocal } from '../domaine/interventions';
import {
  CARACTERES_MINIMUM,
  dateParDefaut,
  filtrerSites,
  libelleDeMachine,
  motPourRecherche,
} from '../domaine/planning';
import {
  FREQUENCES,
  ajouterPieceTournee,
  basculerTechnicienTournee,
  choisirAAffecter,
  choisirSiteTournee,
  cocherMachine,
  estAAffecter,
  formulaireTourneeModifie,
  formulaireTourneeVide,
  libelleCreation,
  lirePieceTournee,
  machinesCocheesDEmblee,
  moisDuFormulaire,
  oublierSiteTournee,
  phraseOccurrences,
  retirerPieceTournee,
  validerFormulaireTournee,
  versEntreeTournee,
  type FormulaireTournee,
} from '../domaine/planning-tournees';
import { quantiteAffichee } from '../domaine/planning-pieces';
import { ChampReference } from './ChampReference';
import { ErreurFormulaire, Panneau } from './Formulaire';

const PAUSE_DE_FRAPPE_MS = 250;
const SITES_AFFICHES = 8;
const MACHINES_DU_SITE = 50;
const PRESENCE_GAMME_PAR_LOT = 6;

/** Une machine du site, avec ce qu'on sait de sa gamme : `null` tant que la lecture n'est pas revenue. */
interface MachineDuSite {
  readonly asset: Asset;
  readonly aUneGamme: boolean | null;
}

export function VoletTournee({ onCreee, onFermer }: { onCreee: (tournee: Tournee) => void; onFermer: () => void }) {
  const initial = useRef<FormulaireTournee>(formulaireTourneeVide(dateParDefaut(aujourdhuiLocal())));
  const [f, setF] = useState<FormulaireTournee>(initial.current);
  /** Rend la création rejouable : une même ouverture du volet ne crée jamais deux tournées. */
  const cle = useRef(crypto.randomUUID());

  const [natures, setNatures] = useState<readonly NatureBon[] | null>(null);
  const [techniciens, setTechniciens] = useState<readonly Technicien[] | null>(null);
  const [erreurChargement, setErreurChargement] = useState<string | null>(null);

  const [saisie, setSaisie] = useState('');
  const [sites, setSites] = useState<readonly Site[] | null>(null);
  const [erreurRecherche, setErreurRecherche] = useState<string | null>(null);

  const [machines, setMachines] = useState<readonly MachineDuSite[] | null>(null);
  const [apercu, setApercu] = useState<{ readonly items: readonly { readonly no: number; readonly date: string }[] } | null>(null);
  const [erreurApercu, setErreurApercu] = useState<string | null>(null);

  const [tente, setTente] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);

  // La pièce en cours de saisie (une seule à la fois : « Ajouter » la range dans la liste).
  const [pieceMachine, setPieceMachine] = useState('');
  const [pieceSaisie, setPieceSaisie] = useState({ designation: '', quantite: '', reference: '', unite: '' });
  const [refusPiece, setRefusPiece] = useState<string | null>(null);

  useEffect(() => {
    let annule = false;
    Promise.all([api.naturesBon(), api.techniciensPlanning()])
      .then(([n, t]) => {
        if (annule) return;
        setNatures(n.filter((x) => !x.archived));
        setTechniciens(t.items.filter((x) => !x.archived));
      })
      .catch((e: unknown) => {
        if (!annule) setErreurChargement(e instanceof ApiError ? e.message : 'Chargement impossible.');
      });
    return () => {
      annule = true;
    };
  }, []);

  // La recherche d'un site part après une pause de frappe ; une réponse périmée est jetée.
  useEffect(() => {
    const mot = motPourRecherche(saisie);
    if (mot === null) {
      setSites(null);
      return;
    }
    let annule = false;
    const minuteur = window.setTimeout(() => {
      api
        .sites(mot)
        .then((s) => {
          if (annule) return;
          setSites(filtrerSites(s, saisie).slice(0, SITES_AFFICHES));
          setErreurRecherche(null);
        })
        .catch((e: unknown) => {
          if (!annule) setErreurRecherche(e instanceof ApiError ? e.message : 'Recherche impossible.');
        });
    }, PAUSE_DE_FRAPPE_MS);
    return () => {
      annule = true;
      window.clearTimeout(minuteur);
    };
  }, [saisie]);

  // Le site choisi : ses machines, puis pour chacune « a-t-elle une gamme ? » (par petits lots, pas cinquante requêtes d'un coup).
  const siteId = f.siteId;
  useEffect(() => {
    if (siteId === null) {
      setMachines(null);
      return;
    }
    let annule = false;
    setMachines(null);
    void (async () => {
      try {
        const liste = (await api.assets({ siteId, take: MACHINES_DU_SITE })).items;
        if (annule) return;
        setMachines(liste.map((asset) => ({ asset, aUneGamme: null })));
        const presence: boolean[] = [];
        for (let i = 0; i < liste.length; i += PRESENCE_GAMME_PAR_LOT) {
          const lot = liste.slice(i, i + PRESENCE_GAMME_PAR_LOT);
          const reponses = await Promise.all(
            lot.map((a) =>
              api
                .assetMaintenance(a.id)
                .then((m) => m?.rattachement != null)
                // Une lecture qui échoue ne bloque rien : la machine reste cochable, simplement non cochée d'emblée.
                .catch(() => false),
            ),
          );
          if (annule) return;
          presence.push(...reponses);
        }
        if (annule) return;
        const lues = liste.map((asset, i) => ({ asset, aUneGamme: presence[i] ?? false }));
        setMachines(lues);
        const cochees = machinesCocheesDEmblee(lues.map((m) => ({ assetId: m.asset.id, aUneGamme: m.aUneGamme === true })));
        setF((courant) => (courant.siteId === siteId ? { ...courant, assetIds: cochees, pieces: [] } : courant));
      } catch {
        if (!annule) setMachines([]);
      }
    })();
    return () => {
      annule = true;
    };
  }, [siteId]);

  // L'aperçu des visites : les dates viennent du serveur (une seule fonction, jamais recalculée ici).
  const mois = moisDuFormulaire(f);
  useEffect(() => {
    if (mois === 'ERREUR' || f.premierJour === '') {
      setApercu(null);
      setErreurApercu(null);
      return;
    }
    let annule = false;
    const minuteur = window.setTimeout(() => {
      api
        .occurrencesTournee(f.premierJour, mois)
        .then((r) => {
          if (annule) return;
          setApercu({ items: r.items });
          setErreurApercu(null);
        })
        .catch((e: unknown) => {
          if (annule) return;
          setApercu(null);
          setErreurApercu(e instanceof ApiError ? e.message : 'Aperçu des interventions impossible.');
        });
    }, PAUSE_DE_FRAPPE_MS);
    return () => {
      annule = true;
      window.clearTimeout(minuteur);
    };
  }, [f.premierJour, mois]);

  const messages = useMemo(() => validerFormulaireTournee(f), [f]);

  const modifier = (suivant: FormulaireTournee) => {
    setF(suivant);
    setErreur(null);
  };

  const fermer = () => {
    if (formulaireTourneeModifie(f, initial.current) && !window.confirm('Abandonner cette tournée ? Ce que vous avez saisi sera perdu.')) return;
    onFermer();
  };

  const machinesCochees = (machines ?? []).filter((m) => f.assetIds.includes(m.asset.id));
  const libelleMachineCochee = (assetId: string): string => {
    const m = (machines ?? []).find((x) => x.asset.id === assetId);
    return m ? libelleDeMachine(m.asset) : 'Machine';
  };
  const machinePiece = f.assetIds.includes(pieceMachine) ? pieceMachine : (f.assetIds[0] ?? '');

  const ajouterPiece = () => {
    if (machinePiece === '') {
      setRefusPiece('Cochez d’abord la machine qui recevra la pièce.');
      return;
    }
    const lue = lirePieceTournee(pieceSaisie);
    if (!lue.ok) {
      setRefusPiece(lue.erreur);
      return;
    }
    modifier(ajouterPieceTournee(f, { assetId: machinePiece, ...lue.piece }));
    setPieceSaisie({ designation: '', quantite: '', reference: '', unite: '' });
    setRefusPiece(null);
  };

  const creer = async () => {
    setTente(true);
    if (messages.length > 0) return;
    setEnCours(true);
    setErreur(null);
    try {
      onCreee(await api.creerTournee(versEntreeTournee(f, cle.current)));
    } catch (e) {
      setErreur(e instanceof ApiError || e instanceof Error ? e : new Error('Création impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  const sansGamme = (machines ?? []).filter((m) => m.aUneGamme === false);

  return (
    <Panneau
      titre="Nouvelle tournée"
      sousTitre="Les mêmes machines d’un même site, tous les N mois : toutes les interventions se créent d’un coup."
      large
      onFermer={fermer}
      actions={
        <>
          <button type="button" className="secondaire" disabled={enCours} onClick={fermer}>
            Annuler
          </button>
          <button type="button" disabled={enCours} onClick={() => void creer()}>
            {enCours ? 'Création…' : libelleCreation(apercu?.items.length ?? null, f.assetIds.length)}
          </button>
        </>
      }
    >
      {erreurChargement && <div className="message erreur">{erreurChargement}</div>}

      {f.siteId === null ? (
        <div className="champ">
          <label htmlFor="tournee-site">Où ?</label>
          <input
            id="tournee-site"
            type="text"
            autoFocus
            autoComplete="off"
            value={saisie}
            placeholder="Le site ou le client : ntn sey, alteo…"
            onChange={(e) => setSaisie(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && saisie !== '') {
                e.preventDefault();
                setSaisie('');
                setSites(null);
              }
            }}
          />
          <p className="aide">Tapez au moins {CARACTERES_MINIMUM} caractères ; plusieurs mots se cumulent. Une tournée est faite sur UN site.</p>
          {erreurRecherche && <div className="message erreur">{erreurRecherche}</div>}
          {sites !== null && sites.length === 0 && <div className="vide">Aucun site ne correspond.</div>}
          {sites !== null && sites.length > 0 && (
            <ul className="liste-choix-machine">
              {sites.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className="choix-machine"
                    onClick={() => {
                      modifier(choisirSiteTournee(f, { id: s.id, libelle: s.name }));
                      setSaisie('');
                      setSites(null);
                    }}
                  >
                    <strong>{s.name}</strong>
                    <span className="aide">{[s.customerName, s.city].filter(Boolean).join(' · ') || 'Sans client ni ville'}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="champ">
          <label>Où ?</label>
          <div className="visite-lieu">
            <strong>{f.siteLibelle}</strong>
            <button type="button" className="secondaire discret" onClick={() => modifier(oublierSiteTournee(f))}>
              Changer
            </button>
          </div>

          <p className="aide visite-groupe">Quelles machines ?</p>
          {machines === null && <p className="aide">Lecture des machines du site et de leurs gammes…</p>}
          {machines !== null && machines.length === 0 && <div className="vide">Ce site n’a aucune machine : une tournée a besoin d’au moins une machine.</div>}
          {machines !== null && machines.length > 0 && (
            <ul className="liste-choix-machine tournee-machines">
              {machines.map(({ asset, aUneGamme }) => {
                const cochee = f.assetIds.includes(asset.id);
                return (
                  <li key={asset.id}>
                    <label className="visite-machine-case">
                      <input
                        type="checkbox"
                        checked={cochee}
                        disabled={enCours}
                        onChange={(e) => modifier(cocherMachine(f, asset.id, e.target.checked))}
                      />
                      <span>
                        <strong>{libelleDeMachine(asset)}</strong>
                        {asset.model && asset.label ? <span className="aide"> · {asset.model}</span> : null}
                        {aUneGamme === false && <span className="etat attente"> sans gamme</span>}
                        {aUneGamme === null && <span className="aide"> lecture de la gamme…</span>}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          {sansGamme.length > 0 && (
            <p className="aide">
              {sansGamme.length === 1 ? 'Une machine n’a' : `${sansGamme.length} machines n’ont`} pas de gamme d’entretien : {sansGamme.length === 1 ? 'elle est décochée' : 'elles sont décochées'} d’emblée
              (aucune pièce à préparer pour elle{sansGamme.length > 1 ? 's' : ''}). Cochez-la{sansGamme.length > 1 ? '-les' : ''} si la tournée doit quand même la passer.
            </p>
          )}
          <p className="aide">
            {f.assetIds.length === 0 ? 'Aucune machine cochée.' : `${f.assetIds.length} machine${f.assetIds.length > 1 ? 's' : ''} cochée${f.assetIds.length > 1 ? 's' : ''}.`}
          </p>
        </div>
      )}

      <div className="champ">
        <label>Quoi ?</label>
        {natures === null ? (
          <p className="aide">Chargement…</p>
        ) : natures.length === 0 ? (
          <p className="aide">Aucune nature de bon : installez les natures de départ depuis « Interventions › Réglages des bons ».</p>
        ) : (
          <div className="chips-secondaires" role="group" aria-label="Nature de la tournée">
            {natures.map((n) => (
              <button
                key={n.id}
                type="button"
                className={`chip${f.natureId === n.id ? ' actif' : ''}`}
                aria-pressed={f.natureId === n.id}
                onClick={() => modifier({ ...f, natureId: n.id })}
              >
                {n.label}
              </button>
            ))}
          </div>
        )}
        <p className="aide">Le palier de chaque machine se choisit intervention par intervention, le moment venu : la proposition d’après les heures est dans le briefing.</p>
      </div>

      <div className="champ">
        <label>À quelle fréquence ?</label>
        <div className="chips-secondaires" role="group" aria-label="Fréquence">
          {FREQUENCES.map((x) => (
            <button
              key={x.cle}
              type="button"
              className={`chip${f.frequence === x.cle ? ' actif' : ''}`}
              aria-pressed={f.frequence === x.cle}
              onClick={() => modifier({ ...f, frequence: x.cle })}
            >
              {x.libelle}
            </button>
          ))}
        </div>
        {f.frequence === 'AUTRE' && (
          <div className="champ">
            <label htmlFor="tournee-mois">Tous les combien de mois ?</label>
            <input
              id="tournee-mois"
              type="text"
              inputMode="numeric"
              value={f.moisTexte}
              placeholder="4"
              onChange={(e) => modifier({ ...f, moisTexte: e.target.value })}
            />
          </div>
        )}
      </div>

      <div className="champ">
        <label htmlFor="tournee-jour">Premier jour</label>
        <input id="tournee-jour" type="date" value={f.premierJour} onChange={(e) => modifier({ ...f, premierJour: e.target.value })} />
        {erreurApercu && <p className="aide erreur-champ">{erreurApercu}</p>}
        {apercu && <p className="aide tournee-apercu">{phraseOccurrences(apercu.items, f.assetIds.length)}</p>}
        <p className="aide">Un samedi ou un dimanche glisse au lundi suivant. Les interventions sont créées sur 12 mois.</p>
      </div>

      <div className="champ">
        <label>Technicien habituel (facultatif)</label>
        {techniciens === null ? (
          <p className="aide">Chargement…</p>
        ) : (
          <div className="chips-secondaires" role="group" aria-label="Techniciens habituels">
            <button
              type="button"
              className={`chip${estAAffecter(f) ? ' actif' : ''}`}
              aria-pressed={estAAffecter(f)}
              title="Les interventions ont une date et personne : elles tombent sur la ligne « À affecter » de la grille"
              onClick={() => modifier(choisirAAffecter(f))}
            >
              À affecter (aucun)
            </button>
            {techniciens.map((t) => {
              const actif = f.technicienIds.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`chip${actif ? ' actif' : ''}`}
                  aria-pressed={actif}
                  onClick={() => modifier(basculerTechnicienTournee(f, t.id))}
                >
                  {t.name}
                </button>
              );
            })}
          </div>
        )}
        <p className="aide">
          Ce n’est pas toujours le même technicien qui y va : sans technicien, chaque intervention a sa date et attend sur la ligne « À affecter ».
          Vous pouvez aussi en cocher plusieurs ; chaque intervention se corrige ensuite une par une.
        </p>
      </div>

      <div className="ligne-champs">
        <div className="champ">
          <label htmlFor="tournee-commande">N° de commande du client (facultatif)</label>
          <input id="tournee-commande" type="text" maxLength={60} value={f.commande} onChange={(e) => modifier({ ...f, commande: e.target.value })} />
          <p className="aide">Saisi une fois : chaque intervention le lit de la tournée.</p>
        </div>
        <div className="champ">
          <label htmlFor="tournee-duree">Durée prévue de chaque intervention (facultative)</label>
          <input
            id="tournee-duree"
            type="text"
            inputMode="decimal"
            value={f.dureeTexte}
            placeholder="8, 7,5, 1h30, 45 min"
            onChange={(e) => modifier({ ...f, dureeTexte: e.target.value })}
          />
          <p className="aide">En heures par défaut. Une estimation, jamais un temps réel.</p>
        </div>
      </div>

      <div className="champ">
        <label htmlFor="tournee-remarque">Remarque de la tournée (facultative)</label>
        <textarea
          id="tournee-remarque"
          rows={2}
          maxLength={1000}
          value={f.remarque}
          placeholder="Ce qui vaut à chaque passage : accès par le quai 3, prévenir le chef d’atelier…"
          onChange={(e) => modifier({ ...f, remarque: e.target.value })}
        />
        <p className="aide">Permanente : elle se lit sur chaque intervention de la tournée et se corrige à un seul endroit.</p>
      </div>

      <div className="champ">
        <label htmlFor="tournee-premiere">Remarque pour la première intervention (facultative)</label>
        <textarea
          id="tournee-premiere"
          rows={2}
          maxLength={1000}
          value={f.remarquePremiere}
          placeholder="Ce mois-ci, pense à regarder la quantité d’huile qui reste sur site…"
          onChange={(e) => modifier({ ...f, remarquePremiere: e.target.value })}
        />
        <p className="aide">Écrite sur la première intervention seulement : les suivantes n’en portent pas.</p>
      </div>

      <div className="planning-pieces-plus visite-pieces-plus tournee-pieces">
        <div className="planning-pieces-entete">
          <strong>Pièces en plus (première intervention)</strong>
          <span className="aide">au cas où : une pièce à apporter, et surtout pour quelle machine — sans montant</span>
        </div>
        {f.pieces.length > 0 && (
          <ul className="visite-pieces-attente">
            {f.pieces.map((p, i) => (
              <li key={`${p.assetId}|${p.reference ?? ''}|${p.designation}|${i}`}>
                <span>
                  <strong>{libelleMachineCochee(p.assetId)}</strong> —{' '}
                  {p.reference ? <span className="ref">{p.reference} · </span> : null}
                  {p.designation} × {quantiteAffichee(p.quantity, p.unit ?? null)}
                </span>
                <button
                  type="button"
                  className="secondaire discret"
                  disabled={enCours}
                  aria-label={`Retirer ${p.designation}`}
                  title="Retirer cette pièce"
                  onClick={() => modifier(retirerPieceTournee(f, i))}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
        {machinesCochees.length === 0 ? (
          <p className="aide">Cochez une machine pour pouvoir lui demander une pièce.</p>
        ) : (
          <div className="planning-pieces-ajout">
            <div className="champ">
              <label htmlFor="tournee-piece-machine">Pour quelle machine ?</label>
              <select id="tournee-piece-machine" value={machinePiece} disabled={enCours} onChange={(e) => setPieceMachine(e.target.value)}>
                {machinesCochees.map((m) => (
                  <option key={m.asset.id} value={m.asset.id}>
                    {libelleDeMachine(m.asset)}
                  </option>
                ))}
              </select>
            </div>
            <ChampReference
              machineId={machinePiece}
              libelle="Référence de la pièce à ajouter"
              valeur={pieceSaisie.reference}
              placeholder="Début de la référence (facultatif)"
              desactive={enCours}
              onChange={(valeur) => {
                setRefusPiece(null);
                setPieceSaisie((s) => ({ ...s, reference: valeur }));
              }}
              onChoisir={(choix) => {
                setRefusPiece(null);
                setPieceSaisie((s) => ({ ...s, reference: choix.reference, designation: s.designation.trim() === '' ? choix.description : s.designation }));
              }}
            />
            <div className="planning-pieces-ajout-ligne">
              <input
                type="text"
                aria-label="Désignation de la pièce à ajouter"
                placeholder="Désignation"
                value={pieceSaisie.designation}
                disabled={enCours}
                maxLength={200}
                onChange={(e) => {
                  setRefusPiece(null);
                  setPieceSaisie((s) => ({ ...s, designation: e.target.value }));
                }}
              />
              <input
                type="text"
                inputMode="decimal"
                aria-label="Quantité de la pièce à ajouter"
                placeholder="Qté"
                className="q"
                value={pieceSaisie.quantite}
                disabled={enCours}
                onChange={(e) => {
                  setRefusPiece(null);
                  setPieceSaisie((s) => ({ ...s, quantite: e.target.value }));
                }}
              />
              <input
                type="text"
                aria-label="Unité de la pièce à ajouter"
                placeholder="Unité"
                className="u"
                value={pieceSaisie.unite}
                disabled={enCours}
                maxLength={20}
                onChange={(e) => {
                  setRefusPiece(null);
                  setPieceSaisie((s) => ({ ...s, unite: e.target.value }));
                }}
              />
              <button type="button" className="secondaire" disabled={enCours} onClick={ajouterPiece}>
                Ajouter à la première intervention
              </button>
            </div>
            {refusPiece && <p className="aide erreur-champ">{refusPiece}</p>}
            <p className="aide">Elles sont enregistrées sur la première intervention, au moment de « {libelleCreation(apercu?.items.length ?? null, f.assetIds.length)} ».</p>
          </div>
        )}
      </div>

      {tente && messages.length > 0 && (
        <div className="message erreur">
          <ul className="liste-messages">
            {messages.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      )}
      <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />
    </Panneau>
  );
}
